import { Box, Button, Chip, CircularProgress, Typography, alpha, useTheme } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SendIcon from '@mui/icons-material/Send'
import DrawIcon from '@mui/icons-material/Draw'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import PaymentIcon from '@mui/icons-material/Payment'
import ForwardIcon from '@mui/icons-material/Forward'
import TimerOffIcon from '@mui/icons-material/TimerOff'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import ViewAgreement from 'components/Deals/contracts/ViewAgreement'
import { fetchViewLink } from 'api/customHooks/fetchViewLink'
import { fetchAuditTrail, type AuditTrailEvent } from 'api/customHooks/fetchAuditTrail'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Product } from 'types'
import { calculateDealTotals } from 'utils/dealCalculations'
import type { VersionedDeal } from 'utils/agreementVersioning'

const fmt = (v: number | string) =>
  typeof v === 'number' ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : v

const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced']

// ─── Audit trail helpers ─────────────────────────────────────────────

/** PandaDoc v2 audit trail action codes → labels */
const ACTION_LABELS: Record<number, string> = {
  1: 'Document Created',
  2: 'New Revision',
  3: 'Approval Requested',
  4: 'Approved',
  5: 'Rejected',
  6: 'Sent',
  7: 'Completed by All',
  8: 'Viewed',
  9: 'Completed',
  10: 'Waiting for Payment',
  11: 'Paid',
  12: 'Forwarded',
  13: 'Expired',
  14: 'Bank Account Submitted',
  15: 'Bank Account Verified',
  16: 'Payment Submitted',
  17: 'Payment Failed',
  18: 'Completed Manually',
  19: 'Expired Manually',
  20: 'Paid Manually',
  21: 'Approval Deleted',
  22: 'Approval Forced',
  23: 'Reassigned',
  24: 'Recipient Edited',
  25: 'Recipient Added',
  26: 'Recipient Deleted',
  27: 'Declined Manually',
  28: 'Approval Step Skipped',
  29: 'Suggest Edits',
  43: 'Declined',
  44: 'Partly Paid',
  45: 'Partly Paid Manually',
  47: 'KBA Verification Passed',
  48: 'ID Verification Passed',
  49: 'SMS Verification Passed',
  50: 'Passcode Verification Passed',
  51: 'KBA Verification Failed',
  52: 'ID Verification Failed',
  53: 'SMS Verification Failed',
  54: 'Passcode Verification Failed',
  55: 'Signed with QES',
  56: 'QES Completed',
}

const ACTION_ICONS: Record<number, typeof VisibilityIcon> = {
  1: NoteAddIcon,
  2: EditIcon,
  6: SendIcon,
  7: CheckCircleIcon,
  8: VisibilityIcon,
  9: DrawIcon,
  4: ThumbUpIcon,
  5: ThumbDownIcon,
  11: PaymentIcon,
  12: ForwardIcon,
  13: TimerOffIcon,
  23: SwapHorizIcon,
  25: PersonAddIcon,
  26: PersonRemoveIcon,
  43: ThumbDownIcon,
}

const ACTION_COLORS: Record<number, string> = {
  1: 'rgb(107, 185, 240)',    // blue - creation
  2: 'rgb(107, 185, 240)',    // blue - revision
  6: 'rgb(170, 130, 240)',    // purple - sent
  7: 'rgb(80, 200, 120)',     // green - completed
  8: 'rgb(255, 183, 77)',     // orange - viewed
  9: 'rgb(80, 200, 120)',     // green - completed
  4: 'rgb(80, 200, 120)',     // green - approved
  5: 'rgb(244, 67, 54)',      // red - rejected
  11: 'rgb(80, 200, 120)',    // green - paid
  12: 'rgb(255, 183, 77)',    // orange - forwarded
  13: 'rgb(158, 158, 158)',   // grey - expired
  23: 'rgb(255, 183, 77)',    // orange - reassigned
  43: 'rgb(244, 67, 54)',     // red - declined
}

function getEventIcon(event: AuditTrailEvent) {
  return ACTION_ICONS[event.action] || HistoryIcon
}

function getEventColor(event: AuditTrailEvent) {
  return ACTION_COLORS[event.action] || 'rgb(158, 158, 158)'
}

function getEventLabel(event: AuditTrailEvent): string {
  return ACTION_LABELS[event.action] || `Action ${event.action}`
}

function formatAuditDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr || ''
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' +
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  } catch {
    return dateStr || ''
  }
}

// ─── Component ───────────────────────────────────────────────────────

type VersionedAgreementPanelProps = {
  version: VersionedDeal
  previousVersion?: VersionedDeal
}

const VersionedAgreementPanel = ({ version, previousVersion }: VersionedAgreementPanelProps) => {
  const theme = useTheme()
  const { deal, products, semanticVersion } = version
  const contractId = deal?.contractId

  const isActive = deal?.stage === 'Deal Won' && !TERMINAL_STATUSES.includes(deal?.dealStatus)

  // ─── PandaDoc PDF state ──────────────────────────────────────────────
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState(false)
  const prevContractIdRef = useRef<string | undefined>(undefined)

  // ─── Audit trail state ───────────────────────────────────────────────
  const [auditTrail, setAuditTrail] = useState<AuditTrailEvent[]>([])
  const [auditLoading, setAuditLoading] = useState(false)

  // Fetch PDF when contractId changes
  useEffect(() => {
    if (!contractId) {
      setPdfBlobUrl(null)
      setPdfLoading(false)
      setPdfError(false)
      prevContractIdRef.current = undefined
      return
    }

    if (contractId === prevContractIdRef.current && pdfBlobUrl) return

    prevContractIdRef.current = contractId
    let cancelled = false

    const loadPdf = async () => {
      setPdfLoading(true)
      setPdfError(false)
      setPdfBlobUrl(prev => {
        if (prev) window.URL.revokeObjectURL(prev)
        return null
      })

      const blob = await fetchViewLink(contractId)

      if (cancelled) return

      if (blob) {
        const url = window.URL.createObjectURL(blob)
        setPdfBlobUrl(url)
      } else {
        setPdfError(true)
      }
      setPdfLoading(false)
    }

    loadPdf()

    return () => {
      cancelled = true
    }
  }, [contractId])

  // Fetch audit trail when contractId changes
  useEffect(() => {
    if (!contractId) {
      setAuditTrail([])
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      const events = await fetchAuditTrail(contractId)
      if (cancelled) return
      // Sort oldest → newest (chronological order)
      const sorted = (events ?? []).sort((a, b) =>
        new Date(a.date_created).getTime() - new Date(b.date_created).getTime()
      )
      setAuditTrail(sorted)
      setAuditLoading(false)
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [contractId])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) window.URL.revokeObjectURL(pdfBlobUrl)
    }
  }, [pdfBlobUrl])

  const handleDownload = useCallback(() => {
    if (!pdfBlobUrl) return
    const link = document.createElement('a')
    link.href = pdfBlobUrl
    link.download = `${deal.name || 'contract'}_v${semanticVersion}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [pdfBlobUrl, deal.name, semanticVersion])

  // Compute change summary against previous version
  const changeSummary = useMemo(() => {
    if (!previousVersion) return null

    const prev = previousVersion.deal
    const diffFields: string[] = []
    for (const f of ['name', 'stage', 'dealStatus', 'startDate', 'endDate', 'type', 'status']) {
      if (String((deal as any)?.[f] ?? '') !== String((prev as any)?.[f] ?? '')) {
        diffFields.push(f)
      }
    }

    const prevNames = new Set(previousVersion.products.map((p: Product) => p?.productName))
    const currNames = new Set(products.map(p => p?.productName))
    const added = products.filter(p => !prevNames.has(p?.productName ?? ''))
    const removed = [...previousVersion.products].filter(p => !currNames.has(p?.productName ?? ''))

    const prevTotals = calculateDealTotals(previousVersion.deal, previousVersion.products)
    const currTotals = calculateDealTotals(deal, products)
    const totalChanged = currTotals.total !== prevTotals.total || currTotals.monthlyTotal !== prevTotals.monthlyTotal

    if (diffFields.length === 0 && added.length === 0 && removed.length === 0 && !totalChanged) return null

    return { diffFields, added, removed, totalChanged, prevTotals, currTotals }
  }, [deal, products, previousVersion])

  const diffBg = alpha(theme.palette.text.link, 0.15)

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Version Header Bar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Chip
          label={`v${semanticVersion}`}
          size="small"
          color="primary"
          sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13, height: 26 }}
        />
        <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
          {deal.name}
        </Typography>
        <Chip
          label={deal.type}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 500,
            color: renderColorByString(deal.type),
            borderColor: renderColorByString(deal.type),
          }}
          variant="outlined"
        />
        {isActive && (
          <Chip label="Active" size="small" color="success" sx={{ height: 20, fontSize: 11 }} />
        )}
        {deal.dealStatus === 'Replaced' && (
          <Chip label="Replaced" size="small" sx={{ height: 20, fontSize: 11, bgcolor: 'warning.light' }} />
        )}
        {contractId && pdfBlobUrl && (
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ ml: 'auto', fontSize: 11, textTransform: 'none' }}
          >
            Download PDF
          </Button>
        )}
      </Box>

      {/* Change Summary Banner */}
      {changeSummary && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            bgcolor: diffBg,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
            Changes from v{previousVersion?.semanticVersion}:
          </Typography>
          {changeSummary.added.length > 0 && (
            <Typography sx={{ fontSize: 11, color: 'success.main' }}>
              +{changeSummary.added.length} service{changeSummary.added.length !== 1 ? 's' : ''}
            </Typography>
          )}
          {changeSummary.removed.length > 0 && (
            <Typography sx={{ fontSize: 11, color: 'error.main' }}>
              -{changeSummary.removed.length} service{changeSummary.removed.length !== 1 ? 's' : ''}
            </Typography>
          )}
          {changeSummary.totalChanged && (
            <Typography sx={{ fontSize: 11, color: 'warning.main' }}>
              Total: {fmt(changeSummary.prevTotals.total)} &rarr; {fmt(changeSummary.currTotals.total)}
            </Typography>
          )}
          {changeSummary.diffFields.length > 0 && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              Fields: {changeSummary.diffFields.join(', ')}
            </Typography>
          )}
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {contractId ? (
          // PandaDoc contract preview + audit trail side by side
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* PDF Section (left, fills remaining space) */}
            <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
              {pdfLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading contract...</Typography>
                </Box>
              ) : pdfError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500 }}>
                    Unable to load contract preview
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                    The PandaDoc document may not be available for download yet.
                  </Typography>
                </Box>
              ) : pdfBlobUrl ? (
                <object
                  data={pdfBlobUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{ display: 'block' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      Your browser does not support inline PDF viewing.
                    </Typography>
                    <Button size="small" onClick={handleDownload}>Download PDF</Button>
                  </Box>
                </object>
              ) : null}
            </Box>

            {/* Audit Trail Panel (right sidebar) */}
            {(auditTrail.length > 0 || auditLoading) && (
              <Box
                sx={{
                  width: 280,
                  minWidth: 280,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  overflowY: 'auto',
                  bgcolor: alpha(theme.palette.text.primary, 0.02),
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                    Audit Trail
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {auditTrail.length} event{auditTrail.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                {auditLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 2 }}>
                    <CircularProgress size={14} />
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Loading...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ px: 1, py: 1 }}>
                    {auditTrail.map((event, idx) => {
                      const Icon = getEventIcon(event)
                      const color = getEventColor(event)
                      const isLast = idx === auditTrail.length - 1

                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            position: 'relative',
                            pl: 0.5,
                          }}
                        >
                          {/* Timeline line + dot */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.25 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${color}20`,
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ fontSize: 14, color }} />
                            </Box>
                            {!isLast && (
                              <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.25 }} />
                            )}
                          </Box>

                          {/* Event content */}
                          <Box sx={{ pb: isLast ? 0.5 : 1.5, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', lineHeight: 1.6 }}>
                              {getEventLabel(event)}
                            </Typography>
                            {event.user?.email && (
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                                {event.user.email}
                              </Typography>
                            )}
                            {event.reason && (
                              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontStyle: 'italic' }}>
                                {event.reason}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                              {formatAuditDate(event.date_created)}
                              {event.ip_address && ` \u00B7 ${event.ip_address}`}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          // No contract attached — show deal editor
          <ViewAgreement dealPK={deal.PK} dealSK={deal.SK} />
        )}
      </Box>
    </Box>
  )
}

export default VersionedAgreementPanel
